const DEFAULT_OPTIONS = {
  root: null,
  rootMargin: '0px 0px -10% 0px',
  threshold: 0.1,
}

const SKIP_SELECTOR = [
  '.home-intro',
  '[data-reveal-skip]',
  '.hero-carousel',
  '.hero-equipo',
  '.hero-caso',
  '.hero-sobre-gs',
  '.hero-img-gstechlab',
  '[class*="hero-"]',
].join(', ')

let observer = null

export function prefersReducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false
}

function getObserver() {
  if (!observer) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        entry.target.classList.add('reveal--visible')
        observer.unobserve(entry.target)
      })
    }, DEFAULT_OPTIONS)
  }
  return observer
}

function shouldSkip(el) {
  return !el || el.matches(SKIP_SELECTOR)
}

function dedupeNested(elements) {
  return elements.filter(
    (el, _, list) => !list.some((other) => other !== el && other.contains(el)),
  )
}

const REVEALABLE_CHILD_TAGS = new Set(['DIV', 'SECTION'])

/** Si una sección contiene franjas con data-reveal-skip, anima sus hijos en lugar del bloque completo (evita romper parallax). */
function expandTargetsWithSkippedDescendants(candidates) {
  return candidates.flatMap((el) => {
    if (!el.querySelector('[data-reveal-skip]')) return [el]

    const children = [...el.children].filter(
      (child) => REVEALABLE_CHILD_TAGS.has(child.tagName) && !shouldSkip(child),
    )
    return children.length > 0 ? children : [el]
  })
}

/** Resuelve la raíz real de la vista (wrapper .page-content o raíz fusionada). */
export function resolvePageRoot(pageEl) {
  if (!pageEl) return null
  if (!pageEl.classList.contains('page-content')) return pageEl

  const onlyChild = pageEl.children.length === 1 ? pageEl.firstElementChild : null
  if (onlyChild && onlyChild.tagName === 'DIV' && !pageEl.id) {
    return onlyChild
  }

  return pageEl
}

/** Detecta bloques principales de cada página para animarlos al entrar en viewport. */
export function collectRevealTargets(pageRoot) {
  if (!pageRoot) return []

  let candidates = [...pageRoot.querySelectorAll(':scope > section, :scope > div[id]')]
    .filter((el) => !shouldSkip(el))

  if (candidates.length === 0) {
    candidates = [...pageRoot.children].filter(
      (el) => el.tagName === 'DIV' && !shouldSkip(el),
    )
  } else if (
    candidates.length === 1
    && candidates[0].tagName === 'DIV'
    && candidates[0].id
    && candidates[0].children.length > 1
  ) {
    const inner = [...candidates[0].children].filter((el) => !shouldSkip(el))
    if (inner.length > 1) candidates = inner
  }

  candidates = expandTargetsWithSkippedDescendants(candidates)
  return dedupeNested(candidates)
}

export function observeElement(el, { variant = 'up', delay = 0 } = {}) {
  if (!el || el.dataset.revealObserved === 'true') return

  const motionVariant = el.dataset.reveal || variant
  const motionDelay = Number(el.dataset.revealDelay) || delay

  el.dataset.revealObserved = 'true'
  el.classList.add('reveal', `reveal--${motionVariant}`)
  if (motionDelay > 0) {
    el.style.setProperty('--reveal-delay', `${motionDelay}ms`)
  }

  if (prefersReducedMotion()) {
    el.classList.add('reveal--visible')
    return
  }

  const obs = getObserver()
  obs.observe(el)
  queueCheckIfAlreadyVisible(el, obs)
}

function queueCheckIfAlreadyVisible(el, obs) {
  requestAnimationFrame(() => {
    if (el.classList.contains('reveal--visible')) return
    const rect = el.getBoundingClientRect()
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight
    const visible = rect.top < viewportHeight * 0.92 && rect.bottom > viewportHeight * 0.08
    if (visible) {
      el.classList.add('reveal--visible')
      obs.unobserve(el)
    }
  })
}

export function observeElements(elements, { variant = 'up', stagger = 0 } = {}) {
  elements.forEach((el, index) => {
    observeElement(el, {
      variant: el.dataset.reveal || variant,
      delay: Number(el.dataset.revealDelay) || (stagger ? index * stagger : 0),
    })
  })
}

export function autoRevealPage(pageEl) {
  const pageRoot = resolvePageRoot(pageEl)
  if (!pageRoot) return

  const targets = collectRevealTargets(pageRoot)
  observeElements(targets, { stagger: 70 })
}

export function refreshPageReveal() {
  autoRevealPage(document.querySelector('.page-content'))
}

export function observeSiteChrome() {
  const footer = document.querySelector('footer')
  if (footer) observeElement(footer, { variant: 'fade' })
}

export function disconnectReveal() {
  observer?.disconnect()
  observer = null
}
